/**
 * Host-side wrapper for the Rubber Band AudioWorklet.
 *
 * Responsibilities:
 *   1. Compile the WASM module once per AudioContext (fetch + compile
 *      happens on the main thread because AudioWorkletGlobalScope
 *      has no `fetch`).
 *   2. Register `rubberbandProcessor.ts` with the AudioContext's
 *      audioWorklet. Idempotent — repeat calls resolve the same
 *      promise.
 *   3. Hand back `RubberBandNode` instances: a thin subclass of
 *      `AudioWorkletNode` with typed `tempoRatio` / `pitchSemitones`
 *      AudioParams and a `ready` promise that resolves once the
 *      processor has initialised its WASM instance.
 *
 * Usage:
 *   ```
 *   const node = await createRubberBandNode(audioCtx, {
 *     tempoRatio: 90 / 140,   // source 90 → project 140 BPM
 *   })
 *   source.connect(node)
 *   node.connect(dest)
 *   // Later, when project BPM drags to 150:
 *   node.tempoRatio.setValueAtTime(90 / 150, audioCtx.currentTime)
 *   ```
 */

import rubberbandWasmUrl from 'rubberband-wasm/dist/rubberband.wasm?url'
// Vite asset-URL import of the processor. The ?url suffix produces
// a string we can pass to `audioWorklet.addModule()`; Vite bundles
// the file as a self-contained worklet script during build and
// serves it as an ES module in dev.
import rubberbandProcessorUrl from './rubberbandProcessor.ts?worker&url'

export interface RubberBandNodeOptions {
  /** Initial tempo ratio (output_samples / input_samples). 1 = no
   *  change. For BPM conform: `sourceBpm / projectBpm`. */
  tempoRatio?: number
  /** Initial pitch offset in semitones. */
  pitchSemitones?: number
  /** Number of channels. Defaults to 2 (stereo). */
  channels?: number
}

export interface RubberBandNode extends AudioWorkletNode {
  readonly tempoRatio: AudioParam
  readonly pitchSemitones: AudioParam
  /** Resolves when the worklet's WASM instance is ready. Until
   *  this resolves the node passes audio through unprocessed —
   *  fine for "any audio is better than silence on attack" but
   *  your tempo change doesn't take effect until the processor
   *  signals ready. */
  readonly ready: Promise<void>
  /** Reset the Rubber Band engine state. Clears internal latency
   *  + output ring so a fresh sample starts clean. */
  resetEngine: () => void
}

// ── Module / worklet caching per AudioContext ─────────────────

interface ContextState {
  /** The compiled WASM module, shared across every RubberBandNode
   *  created on this context. */
  wasmModulePromise: Promise<WebAssembly.Module> | null
  /** The worklet-module-registration promise. Resolves once the
   *  processor JS has been loaded + `registerProcessor` has fired. */
  workletModulePromise: Promise<void> | null
}

const contextState = new WeakMap<BaseAudioContext, ContextState>()

function getOrInitState(ctx: BaseAudioContext): ContextState {
  let state = contextState.get(ctx)
  if (!state) {
    state = { wasmModulePromise: null, workletModulePromise: null }
    contextState.set(ctx, state)
  }
  return state
}

async function ensureWasmModule(
  ctx: BaseAudioContext,
): Promise<WebAssembly.Module> {
  const state = getOrInitState(ctx)
  if (!state.wasmModulePromise) {
    state.wasmModulePromise = (async () => {
      try {
        return await WebAssembly.compileStreaming(fetch(rubberbandWasmUrl))
      } catch {
        // `compileStreaming` requires correct MIME type from the
        // server; fall back to plain compile for local-dev static
        // servers that serve .wasm as application/octet-stream.
        const res = await fetch(rubberbandWasmUrl)
        const bytes = await res.arrayBuffer()
        return WebAssembly.compile(bytes)
      }
    })()
  }
  return state.wasmModulePromise
}

async function ensureWorkletModule(ctx: BaseAudioContext): Promise<void> {
  const state = getOrInitState(ctx)
  if (!state.workletModulePromise) {
    state.workletModulePromise = ctx.audioWorklet.addModule(
      rubberbandProcessorUrl,
    )
  }
  return state.workletModulePromise
}

/**
 * Create a Rubber Band worklet node on the given AudioContext.
 *
 * The returned node is an `AudioWorkletNode` + `{ tempoRatio,
 * pitchSemitones, ready, resetEngine }`. Connect it inline with any
 * source:
 *
 *   source → rbNode → destination
 *
 * The first call in a context does the WASM compile + worklet
 * module registration (one-time, ~100 ms). Subsequent calls reuse
 * the cached promises.
 */
export async function createRubberBandNode(
  ctx: BaseAudioContext,
  options: RubberBandNodeOptions = {},
): Promise<RubberBandNode> {
  const channels = Math.max(1, Math.min(2, options.channels ?? 2))
  // Compile + module registration run in parallel — they're
  // independent network fetches.
  const [wasmModule] = await Promise.all([
    ensureWasmModule(ctx),
    ensureWorkletModule(ctx),
  ])

  // Construct the node. Parameter data seeds initial values for
  // the AudioParams; later changes go through `.value =` or
  // `setValueAtTime()`.
  const node = new AudioWorkletNode(ctx, 'rubberband-processor', {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [channels],
    parameterData: {
      tempoRatio: options.tempoRatio ?? 1,
      pitchSemitones: options.pitchSemitones ?? 0,
    },
  }) as AudioWorkletNode

  // Readiness handshake. The processor initialises its WASM on
  // receipt of `init-wasm`, then posts `{ type: 'ready' }`. Until
  // then the processor passes audio through unprocessed.
  let resolveReady: () => void = () => {}
  let rejectReady: (err: Error) => void = () => {}
  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve
    rejectReady = reject
  })

  node.port.onmessage = e => {
    const data = e.data
    if (data?.type === 'ready') resolveReady()
    else if (data?.type === 'error') rejectReady(new Error(data.message))
  }

  node.port.postMessage({
    type: 'init-wasm',
    wasmModule,
    channels,
  })

  const tempoRatio = node.parameters.get('tempoRatio')
  const pitchSemitones = node.parameters.get('pitchSemitones')
  if (!tempoRatio || !pitchSemitones) {
    throw new Error(
      'rubberband-processor missing expected AudioParams; check that the worklet module loaded correctly.',
    )
  }

  // Mutate the node to expose typed fields. The tempoRatio /
  // pitchSemitones properties shadow AudioWorkletNode.parameters
  // so callers can write `node.tempoRatio.value = 0.8` directly.
  const rbNode = node as RubberBandNode
  Object.defineProperties(rbNode, {
    tempoRatio: { value: tempoRatio, enumerable: true, writable: false },
    pitchSemitones: {
      value: pitchSemitones,
      enumerable: true,
      writable: false,
    },
    ready: { value: ready, enumerable: true, writable: false },
    resetEngine: {
      value: () => node.port.postMessage({ type: 'reset' }),
      enumerable: true,
      writable: false,
    },
  })
  return rbNode
}

/**
 * Eagerly warm the WASM compile + worklet-module load for a given
 * context. Useful at app bootstrap so the first sample hit that
 * needs the worklet doesn't pay the cold-start cost (~100 ms).
 * Non-blocking for the caller.
 */
export function warmRubberBandWorklet(ctx: BaseAudioContext): void {
  void ensureWasmModule(ctx)
  void ensureWorkletModule(ctx)
}
